'use client';


import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { Box, Card, Stack, Skeleton, CardContent } from '@mui/material';

import { sortPosts, SORT_TYPES } from 'src/utils/moments/view/post-sort';
import { getRepostTemplatePosts } from 'src/utils/moments/domain/moment-ingest';

import { RADIUS } from 'src/theme/ui-tokens';
import { MOMENT_FEED_PAGE_SIZE } from 'src/constants/moment-query';

import PostCard from 'src/components/cmui/post-card/PostCard';
import { NoData } from 'src/components/cmui/exception/no-data';
import PostTable from 'src/components/cmui/post-card/PostTable';
import PostDetail from 'src/components/cmui/post-card/PostDetail';
import PostCardList from 'src/components/cmui/post-card/PostCardList';
import PostCardRepost from 'src/components/cmui/post-card/PostCardRepost';
import PostMediaDetail from 'src/components/cmui/post-card/post-media-detail';
import PostCardListRepost from 'src/components/cmui/post-card/PostCardListRepost';


const DEFAULT_PAGE_SIZE = MOMENT_FEED_PAGE_SIZE;
const MIN_CARD_WIDTH = 240;
const MAX_COLUMN_COUNT = 4;

function estimateColumnCount(width: number) {
  const colGap = 20; // sx gap: 2.5 (≈20px)
  const fit = Math.floor((width + colGap) / (MIN_CARD_WIDTH + colGap));
  return Math.max(1, Math.min(MAX_COLUMN_COUNT, fit));
}

/**
 * 게시물 피드 — 목록/격자 두 모양, 하이라이트·필터·페이지네이션을 함께 다룬다.
 *
 * ⚠️ **JSDoc이 계약이다.** 배열·Set 프롭은 기본값이 `[]`·`new Set()` 이라 타입이
 * `never[]`·`Set<never>` 로 좁혀진다 — `.tsx`·`.ts` 소비자가 문자열 배열을 넘기면
 * 그대로 막힌다 — 아래 `@param` 목록을 지우지 않는다.
 *
 * @param {object} props
 * @param {object[]} props.posts
 * @param {string} [props.searchKeyword] 노랗게 칠할 말 (쉼표로 여러 개)
 * @param {string} [props.clientSearch] 카테고리 키워드 (쉼표로 여러 개)
 * @param {string[]} [props.topicKeywords]
 *   주제 필터 겸 하이라이트. `disableTopicFilter` 가 참이면 **하이라이트에만** 쓴다
 * @param {boolean} [props.disableTopicFilter] 부르는 쪽이 이미 걸렀을 때 켠다
 * @param {string[]} [props.brandKeywords]
 * @param {Set<string>} [props.expandedSelectedBrands]
 * @param {'grid'|'list'|'table'} [props.viewMode]
 *   ⚠️ **셋이다.** 본문에 `viewMode === 'table'` 갈래가 있고(표 형태 목록)
 *   `sections/idea` 가 그것을 넘긴다 — 계약을 둘로 좁히면 `.tsx` 소비자가 막힌다.
 *   ⛔ **섬네일은 여기 없다** — 그것은 소재 찾기의 정렬 배치 격자(`MediaGrid`)이고
 *   `moment-content` 가 그 보기일 때 이 부품 대신 그것을 그린다
 * @param {number} [props.page] 1-based. 주면 제어 컴포넌트가 된다
 * @param {(page: number) => void} [props.onPageChange]
 * @param {number} [props.pageSize]
 * @param {string} [props.currentSort]
 * @param {(post: object) => void} [props.onClickPost]
 * @param {'post'|'media'} [props.detail]
 *   글을 누르면 무엇을 여나 — `media` 면 **소재 상세**(`PostMediaDetail` · 이전/다음은 화면에 편 순서),
 *   `post` 면 글 상세(`PostDetail`). ⚠️ `post` 는 은퇴할 화면(대시보드·주간 리포트)만 쓴다 — 새 자리는 `media` 다
 * @param {boolean} [props.preserveOrder] 부르는 쪽이 정렬을 끝냈을 때 켠다
 * @param {boolean} [props.disableTableSort]
 * @param {boolean} [props.disableRowKeywordHighlight]
 * @param {boolean} [props.hideCountry]
 * @param {boolean} [props.enableImageBookmark]
 * @param {boolean} [props.enableCampaignAdd]
 * @param {boolean} [props.isBookmark]
 * @param {Set<string>} [props.selectedPostIds]
 * @param {(id: string) => void} [props.onToggleSelect]
 * @param {(post: object) => import('react').ReactNode} [props.renderListLeading]
 * @param {(post: object) => import('react').ReactNode} [props.renderGridLeading]
 * @param {boolean} [props.expandReposts]
 * @param {boolean} [props.detailUsesMetricDetailSort]
 * @param {boolean} [props.isLoading]
 * @param {React.ReactNode} [props.emptyTitle] 0건일 때 적는 말
 * @param {React.ReactNode} [props.emptyDescription]
 */
export default function PostFeed({
  posts,
  searchKeyword = '',
  clientSearch = '',
  topicKeywords = [],
  disableTopicFilter = false,
  expandedSelectedBrands = new Set(),
  viewMode = 'grid',
  page: controlledPage,
  pageSize = DEFAULT_PAGE_SIZE,
  currentSort = SORT_TYPES.LATEST,
  onClickPost,
  detail = 'post',
  preserveOrder = false,
  disableTableSort = false,
  disableRowKeywordHighlight = false,
  hideCountry = false,
  enableImageBookmark = true,
  /** true면 북마크 옆 캠페인 추가 버튼 표시 (brand-view 전용) */
  enableCampaignAdd = false,
  isBookmark = false,
  selectedPostIds = new Set(),
  onToggleSelect,
  renderListLeading,
  renderGridLeading,
  /** true면 posts의 reposts[]를 개별 포스트로 펼쳐서 렌더링 */
  expandReposts = true,
  /** 커스텀 서치 등: 상세 드로어에서 metricsSort 반영 */
  detailUsesMetricDetailSort = false,
  /** true면 데이터 수집 중 스켈레톤을 즉시 표시 (탭 전환 지연 없음) */
  isLoading = false,
  emptyTitle = '이 조건에 맞는 게시물이 없습니다',
  emptyDescription = '조건을 넓히거나 기간을 늘려 보세요',
}: {
  posts: any[]
  /** ⚠️ JSDoc 은 `string` 이지만 본문이 `typeof !== 'string'` 갈래를 갖고 있고 실제로 배열도 온다 */
  searchKeyword?: string | readonly string[]
  clientSearch?: string
  topicKeywords?: readonly string[]
  disableTopicFilter?: boolean
  brandKeywords?: readonly string[]
  expandedSelectedBrands?: ReadonlySet<string>
  viewMode?: 'grid' | 'list' | 'table'
  /** 1-based. 주면 제어 컴포넌트가 된다 */
  page?: number
  onPageChange?: (page: number) => void
  pageSize?: number
  currentSort?: string
  onClickPost?: (post: any) => void
  detail?: 'post' | 'media'
  preserveOrder?: boolean
  disableTableSort?: boolean
  disableRowKeywordHighlight?: boolean
  hideCountry?: boolean
  enableImageBookmark?: boolean
  enableCampaignAdd?: boolean
  isBookmark?: boolean
  selectedPostIds?: ReadonlySet<string>
  onToggleSelect?: (id: string) => void
  /** ⚠️ JSDoc 은 `(post)` 한 인자지만 렌더 코드는 `(post, idx)` 로 부른다 — 실호출대로 넓힌다 */
  renderListLeading?: (post: any, idx: number) => React.ReactNode
  renderGridLeading?: (post: any, idx: number) => React.ReactNode
  expandReposts?: boolean
  detailUsesMetricDetailSort?: boolean
  isLoading?: boolean
  /** 0건일 때 적는 말 — 화면마다 조건이 달라 부르는 쪽이 고쳐 쓴다 */
  emptyTitle?: React.ReactNode
  emptyDescription?: React.ReactNode
}) {

  const [uncontrolledPage, setUncontrolledPage] = useState(1);
  const page = controlledPage ?? uncontrolledPage;
  const [selectedPost, setSelectedPost] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const postListTopRef = useRef(null);
  const [skeletonVisible, setSkeletonVisible] = useState(false);
  const postsChangeCountRef = useRef(0);

  const preprocessedPosts = useMemo(() => {
    if (!expandReposts) return posts
    return getRepostTemplatePosts(posts)
  }, [expandReposts, posts])


  // 키워드 입력 정리 (클러스터 확장 없이 원본 값 그대로 사용)
  const expandedKeywords = useMemo(() => {
    const normalizedTopicKeywords = Array.isArray(topicKeywords) ? topicKeywords : [];
    const normalizedSearchKeyword = typeof searchKeyword === 'string' ? searchKeyword : String(searchKeyword ?? '');
    const normalizedClientSearch = typeof clientSearch === 'string' ? clientSearch : String(clientSearch ?? '');

    return {
      expandedSearchKeyword: normalizedSearchKeyword,
      expandedClientSearch: normalizedClientSearch,
      expandedTopicKeywords: normalizedTopicKeywords,
    };
  }, [searchKeyword, clientSearch, topicKeywords]);

  const gridRef = useRef<HTMLDivElement | null>(null);
  const [columnCount, setColumnCount] = useState(() => {
    if (typeof window === 'undefined') return 1
    return estimateColumnCount(window.innerWidth || 0)
  })

  const skeletonCount = pageSize

  const computeColumnsFromWidth = useCallback(() => {
    const el = gridRef.current;
    const width = el?.clientWidth || 0;
    if (!width) return;
    const styles = window.getComputedStyle(el!);
    const colGap = parseFloat(styles.getPropertyValue('column-gap')) || 0;
    const fit = Math.floor((width + colGap) / (MIN_CARD_WIDTH + colGap));
    const next = Math.max(1, Math.min(MAX_COLUMN_COUNT, fit));
    setColumnCount((prev) => (prev !== next ? next : prev));
  }, []);

  // posts가 변경될 때만 페이지를 1로 리셋
  useEffect(() => {
    if (controlledPage == null) {
      setUncontrolledPage(1);
    }
  }, [preprocessedPosts.length, controlledPage]);

  /*
    🚨 **「아직 안 불러왔다」와 「불러왔는데 0건」을 가른다.** 안 가르면 0건일 때 스켈레톤이
      영영 남아 화면이 끝나지 않는다 — 실측으로 키워드뷰·검색뷰의 본문이 그랬다.
    ⚠️ 시작 전에도 `isLoading` 이 거짓이라 이 표시가 없으면 첫 그림에 「없습니다」가 번쩍인다.
  */
  const [loadedOnce, setLoadedOnce] = useState(false);
  useEffect(() => {
    if (isLoading) setLoadedOnce(true);
  }, [isLoading]);
  const isEmpty = !isLoading && loadedOnce && preprocessedPosts.length === 0;

  // Avoid skeleton single-column flash by revealing after layout settles
  useEffect(() => {
    if (preprocessedPosts.length > 0) {
      setSkeletonVisible(false);
      return undefined;
    }
    if (isLoading) {
      setSkeletonVisible(true);
      return undefined;
    }
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setSkeletonVisible(true));
    });
    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [preprocessedPosts.length, isLoading]);

  // 클라이언트 검색 + 토픽 키워드 필터링
  // 주의: clientSearch는 하이라이트용으로만 사용하고 필터링에는 사용하지 않음
  const filteredPosts = useMemo(() => {
    let result = preprocessedPosts;

    // 1. 클라이언트 검색어 필터링은 제거 (하이라이트용으로만 사용)
    // clientSearch는 하이라이트에만 사용되므로 필터링에서 제외

    // 2. 토픽 키워드 필터링
    if (!disableTopicFilter && Array.isArray(topicKeywords) && topicKeywords.length > 0) {
      result = result.filter((post: any) => {
        const fields = [
          post?.title || '',
          post?.content || '',
          post?.author || '',
          post?.account || ''
        ].map(field => String(field).toLowerCase());

        return topicKeywords.some((keyword: string) =>
          fields.some(field => field.includes(keyword.toLowerCase()))
        );
      });
    }

    return result;
  }, [preprocessedPosts, topicKeywords, disableTopicFilter]);  // clientSearch 의존성 제거

  // 정렬: preserveOrder=true 이면 상위에서 정렬된 순서를 유지
  const sortedPosts = useMemo(() => {
    const result = preserveOrder ? filteredPosts : sortPosts(filteredPosts, currentSort);
    return result;
  }, [filteredPosts, currentSort, preserveOrder]);

  const pagedPosts = useMemo(() => {
    const start = (page - 1) * pageSize;
    const sliced = sortedPosts.slice(start, start + pageSize);

    return sliced;
  }, [sortedPosts, page, pageSize]);

  const gridSx = useMemo(() => ({
    width: '100%',
    display: 'grid',
    gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
    gap: 2.5,
    alignItems: 'start',
    mb: 6,
    mt: -2,
    boxSizing: 'border-box',
    maxWidth: '100%',
    overflowX: 'hidden',
  }), [columnCount]);

  // 제거: 디버그 로그
  useEffect(() => {
    postsChangeCountRef.current += 1;
  }, [preprocessedPosts, page, pageSize]);

  useEffect(() => {
    if (viewMode !== 'grid') return undefined;

    computeColumnsFromWidth();

    const grid = gridRef.current;
    if (!grid) return undefined;

    let resizeTimeout: ReturnType<typeof setTimeout> | undefined;
    const ro = new ResizeObserver(() => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(computeColumnsFromWidth, 100);
    });
    ro.observe(grid);

    return () => {
      clearTimeout(resizeTimeout);
      ro.disconnect();
    };
  }, [viewMode, computeColumnsFromWidth, preprocessedPosts, page, pageSize]);

  const handlePostClick = useCallback((post: any) => {
    setSelectedPost(post);
    setIsDetailOpen(true);
    try {
      if (typeof onClickPost === 'function') onClickPost(post);
    } catch { /* noop */ }
  }, [onClickPost]);

  const handleDetailClose = useCallback(() => {
    setIsDetailOpen(false);
    setSelectedPost(null);
  }, []);

  const renderGridPost = useCallback((post: any, idx: number) => {
    const isQuote = Boolean(post?.isQuote);
    const isRepost = Boolean(post?.isRepost);
    const CardComponent = (isQuote || isRepost) ? PostCardRepost : PostCard;
    const cardEl = (
      <CardComponent
        post={post}
        onClick={handlePostClick}
        searchKeyword={expandedKeywords.expandedSearchKeyword}
        clientSearch={expandedKeywords.expandedClientSearch}
        topicKeywords={expandedKeywords.expandedTopicKeywords}
        expandedSelectedBrands={expandedSelectedBrands}
        enableImageBookmark={enableImageBookmark}
        enableCampaignAdd={enableCampaignAdd}
        isBookmark={isBookmark}
        selectedPostIds={selectedPostIds}
        onToggleSelect={onToggleSelect}
      />
    );
    const showLeading = renderGridLeading && !isBookmark;
    return (
      <Box
        key={post.id || post.link || `post-${idx}`}
        sx={{
          my: 0,
          py: 0,
          display: 'flex',
          alignItems: 'flex-start',
          flexDirection: showLeading ? 'column' : undefined,
          gap: showLeading ? 0.5 : undefined,
          minWidth: 0,
        }}
      >
        {showLeading ? (
          <>
            <Box sx={{ alignSelf: 'flex-start' }} onClick={(e) => e.stopPropagation()}>
              {renderGridLeading(post, idx)}
            </Box>
            {cardEl}
          </>
        ) : (
          cardEl
        )}
      </Box>
    );
  }, [
    handlePostClick,
    expandedKeywords,
    expandedSelectedBrands,
    enableImageBookmark,
    enableCampaignAdd,
    isBookmark,
    selectedPostIds,
    onToggleSelect,
    renderGridLeading,
  ]);

  return (
    <Box sx={{ minHeight: 400, mt: "20px", width: '100%' }}>
      <div ref={postListTopRef} />

      {/* 뷰 모드에 따라 다른 레이아웃 표시 */}
      {viewMode === 'table' ? (
        <Stack spacing={0} sx={{ mt: '-20px' }}>
          <PostTable
            posts={sortedPosts}
            searchKeyword={expandedKeywords.expandedSearchKeyword}
            clientSearch={expandedKeywords.expandedClientSearch}
            topicKeywords={expandedKeywords.expandedTopicKeywords}
            currentSort={currentSort}
            page={page}
            perPage={pageSize}
            onClickPost={onClickPost}
            detail={detail}
            defaultSortField="date"
            defaultSortDirection="desc"
            disableSort={disableTableSort}
            disableRowKeywordHighlight={disableRowKeywordHighlight}
            hideCountry={hideCountry}
            enableImageBookmark={enableImageBookmark}
            enableCampaignAdd={enableCampaignAdd}
            isBookmark={isBookmark}
            selectedPostIds={selectedPostIds}
            onToggleSelect={onToggleSelect}
            detailUsesMetricDetailSort={detailUsesMetricDetailSort}
          />
        </Stack>
      ) : viewMode === 'list' ? (
        // 리스트 뷰 - 데이터 없을 때 스켈레톤 표시 추가
        <Stack spacing={0} sx={{ mt: '-30px' }}>
          {isEmpty ? (
            <NoData title={emptyTitle} description={emptyDescription} />
          ) : pagedPosts.length > 0 && !isLoading ? (
            pagedPosts.map((post, idx) => {
              // 전처리 단계에서 설정된 플래그 사용
              const isQuote = Boolean(post?.isQuote);
              const isRepost = Boolean(post?.isRepost);

              // 디버깅: 리포스트/쿼트 감지 로그
              // (debug log removed)

              const ListCardComponent = (isQuote || isRepost) ? PostCardListRepost : PostCardList;

              const listCard = (
                <ListCardComponent
                  post={post}
                  onClick={handlePostClick}
                  searchKeyword={expandedKeywords.expandedSearchKeyword}
                  clientSearch={expandedKeywords.expandedClientSearch}
                  topicKeywords={expandedKeywords.expandedTopicKeywords}
                  expandedSelectedBrands={expandedSelectedBrands}
                  index={idx}
                  enableImageBookmark={enableImageBookmark}
                  enableCampaignAdd={enableCampaignAdd}
                  isBookmark={isBookmark}
                  selectedPostIds={selectedPostIds}
                  onToggleSelect={onToggleSelect}
                />
              );

              const rowKey = post.id || post.link || `post-${idx}`;
              const showLeading = renderListLeading && !isBookmark;
              // 캡처 표식 — 글 한 건(`post`) · 글 주소(`data-capture-key`) · 그림이 있는 글(`data-capture-thumb`):
              // 메일 캡처가 주입한 글과 맞춰 수치 있는 글 → 그림 있는 글 차례로 고른다
              const captureMarks = {
                'data-capture': 'post',
                'data-capture-key': post.link || post.id || undefined,
                'data-capture-thumb': post.imageS3Url || post.imageUrl ? '' : undefined,
              };

              return showLeading ? (
                <Stack key={rowKey} {...captureMarks} direction="row" alignItems="flex-start" spacing={1} sx={{ width: '100%' }}>
                  <Box sx={{ pt: 1.5, flexShrink: 0 }}>{renderListLeading(post, idx)}</Box>
                  <Box sx={{ minWidth: 0, flex: 1, width: '100%' }}>{listCard}</Box>
                </Stack>
              ) : (
                <Box key={rowKey} {...captureMarks} sx={{ width: '100%' }}>
                  {listCard}
                </Box>
              );
            })
          ) : (
            Array.from({ length: Math.min(pageSize, 8) }).map((_, idx) => (
              <Card
                key={`list-skeleton-${idx}`}
                sx={{
                  borderRadius: 0,
                  bgcolor: 'white',
                  border: 'none',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                  boxShadow: 'none',
                  overflow: 'hidden',
                  pb: 2,
                  pt: 2,
                  width: '100%',
                }}
              >
                <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="stretch" sx={{ width: '100%' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Skeleton variant="circular" width={26} height={26} />
                        <Skeleton variant="text" width="20%" height={18} />
                        <Skeleton variant="text" width="30%" height={14} />
                      </Stack>
                      <Skeleton variant="text" width="70%" height={22} />
                      <Skeleton variant="text" width="95%" height={16} />
                      <Skeleton variant="text" width="80%" height={16} />
                      <Stack direction="row" spacing={1}>
                        <Skeleton variant="rounded" width={48} height={22} />
                        <Skeleton variant="rounded" width={48} height={22} />
                        <Skeleton variant="rounded" width={48} height={22} />
                      </Stack>
                    </Stack>
                  </Box>
                  <Box sx={{ width: { xs: 90, sm: 120 }, display: { xs: 'none', sm: 'block' } }}>
                    <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: RADIUS.sm }} />
                  </Box>
                </Stack>
              </Card>
            ))
          )}
        </Stack>
      ) : (
        // 그리드 뷰 — 반응형 CSS Grid (좌→우, 상→하 순서)
        <>
          {isEmpty ? (
            <NoData title={emptyTitle} description={emptyDescription} />
          ) : pagedPosts.length > 0 && !isLoading ? (
            <Box ref={gridRef} sx={gridSx}>
              {pagedPosts.map((post, idx) => renderGridPost(post, idx))}
            </Box>
          ) : (
            <Box
              ref={gridRef}
              sx={{
                ...gridSx,
                visibility: skeletonVisible ? 'visible' : 'hidden',
                opacity: skeletonVisible ? 1 : 0,
              }}
            >
              {Array.from({ length: skeletonCount }).map((_, idx) => (
                <Box key={`skeleton-${idx}`}>
                  <Card sx={{ borderRadius: RADIUS.lg, width: '100%', p: 1.5, boxSizing: 'border-box' }}>
                    <CardContent>
                      <Stack spacing={1}>
                        <Skeleton variant="rectangular" height={140} sx={{ borderRadius: RADIUS.lg }} />
                        <Skeleton variant="text" width="60%" height={28} />
                        <Skeleton variant="text" width="80%" height={20} />
                        <Skeleton variant="text" width="40%" height={20} />
                        <Stack direction="row" spacing={1}>
                          <Skeleton variant="circular" width={24} height={24} />
                          <Skeleton variant="circular" width={24} height={24} />
                          <Skeleton variant="circular" width={24} height={24} />
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}

      {/* 글을 누르면 여는 상세 — 표 보기는 `PostTable` 이 제 정렬 순서로 연다 */}
      {detail === 'media' && viewMode !== 'table' && (
        <PostMediaDetail
          post={selectedPost}
          posts={pagedPosts}
          open={isDetailOpen}
          onClose={handleDetailClose}
          searchKeyword={expandedKeywords.expandedSearchKeyword}
          topicKeywords={expandedKeywords.expandedTopicKeywords}
          clientSearch={expandedKeywords.expandedClientSearch}
          isBookmark={isBookmark}
          enableCampaignAdd={enableCampaignAdd}
          detailUsesMetricDetailSort={detailUsesMetricDetailSort}
        />
      )}
      {detail === 'post' && <PostDetail
        post={selectedPost}
        open={isDetailOpen}
        onClose={handleDetailClose}
        searchKeyword={expandedKeywords.expandedSearchKeyword}
        topicKeywords={expandedKeywords.expandedTopicKeywords}
        clientSearch={expandedKeywords.expandedClientSearch}
        isBookmark={isBookmark}
        enableCampaignAdd={enableCampaignAdd}
        detailUsesMetricDetailSort={detailUsesMetricDetailSort}
      />}
    </Box>
  );
}
